import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Table,
  Select,
  InputNumber,
  Typography,
  Divider,
  message,
  Alert,
  Modal,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  InboxOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { stockService, StockLot } from '../../services/stockService';
import { CustomerSelect } from '../customers';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface StockOutItem {
  id?: number;
  voucher_id?: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id: number;
  lot_number: string;
  expiry_date: string;
  total_amount?: number;
  notes?: string;
  available_stock?: number;
  lot_quantity?: number;
}

interface StockOutRecord {
  id: number;
  reference_number?: string;
  voucher_number?: string;
  customer_id?: number;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  total_items?: number;
  total_amount?: number;
  status?: 'pending' | 'completed' | 'cancelled';
  created_at?: string;
  completed_at?: string;
  created_by?: string;
  notes?: string;
  items?: StockOutItem[];
  // For API detail response structure
  voucher?: {
    id: number;
    voucher_number: string;
    customer_id?: number;
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    notes: string;
    created_by: string;
    created_at: string;
    status: string;
  };
}

interface Medicine {
  id: number;
  name: string;
  code: string;
  unit: string;
  current_stock: number;
  min_stock_level: number;
  max_stock_level: number;
}

interface StockOutModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  editingRecord: StockOutRecord | null;
  medicines: Medicine[];
  isLoadingDetail?: boolean;
}

const StockOutModal: React.FC<StockOutModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingRecord,
  medicines,
  isLoadingDetail = false
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<StockOutItem[]>([]);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | null>(null);
  const [selectedStockLots, setSelectedStockLots] = useState<StockLot[]>([]);
  const [itemForm] = Form.useForm();
  const [customers, setCustomers] = useState<any[]>([]);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialItems, setInitialItems] = useState<StockOutItem[]>([]);
  const [isPromotion, setIsPromotion] = useState(false);

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (visible) {
      if (editingRecord && !isLoadingDetail) {
        // Editing mode - load existing data
        // Check if editingRecord has voucher structure (from API detail) or direct structure (from list)
        const voucherData = editingRecord.voucher || editingRecord;
        const itemsData = editingRecord.items || [];
        
        console.log('Loading editing record:', editingRecord);
        console.log('Voucher data:', voucherData);
        console.log('Items data:', itemsData);
        console.log('Items with IDs:', itemsData.filter(item => item.id).map(item => ({
          id: item.id,
          medicine_name: item.medicine_name
        })));
        
        // Set form values including customer information
        form.setFieldsValue({
          customer_id: voucherData.customer_id || null,
          customer_name: voucherData.customer_name,
          customer_phone: voucherData.customer_phone,
          customer_address: voucherData.customer_address,
          notes: voucherData.notes
        });
        
        // If customer exists in our list, update the customers state
        if (voucherData.customer_name && customers.length > 0) {
          const existingCustomer = customers.find(c => c.name === voucherData.customer_name);
          if (!existingCustomer) {
            // Add the customer to the list if not exists
            setCustomers(prev => [...prev, {
              id: 0, // Temporary ID
              name: voucherData.customer_name,
              phone: voucherData.customer_phone,
              address: voucherData.customer_address
            }]);
          }
        }
        
        // Map items with proper structure for editing
        const mappedItems = itemsData.map(item => ({
          ...item,
          id: item.id, // Ensure ID is preserved
          total_amount: item.quantity * item.unit_price, // Calculate total_amount if not present
          available_stock: item.available_stock || 0
        }));
        
        console.log('Mapped items for editing:', mappedItems.map(item => ({
          id: item.id,
          medicine_name: item.medicine_name,
          quantity: item.quantity,
          hasId: !!item.id
        })));
        
        setItems(mappedItems);
      } else if (!editingRecord) {
        // Create mode - reset form
        form.resetFields();
        setItems([]);
      }
    }
  }, [visible, editingRecord, form, isLoadingDetail]);

  // Debug customers state
  useEffect(() => {
    console.log('Customers state updated:', customers);
  }, [customers]);

  // Lưu snapshot dữ liệu ban đầu khi mở modal
  useEffect(() => {
    if (visible && !isLoadingDetail) {
      setInitialFormValues(form.getFieldsValue());
      setInitialItems([...items]);
    }
  }, [visible, isLoadingDetail]);

  // Cảnh báo khi đóng trình duyệt nếu có dữ liệu chưa lưu
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [form, items, initialFormValues, initialItems]);

  // Hàm kiểm tra dữ liệu đã thay đổi chưa
  const isDirty = () => {
    if (!initialFormValues) return false;
    const currentForm = form.getFieldsValue();
    return (
      JSON.stringify(currentForm) !== JSON.stringify(initialFormValues) ||
      JSON.stringify(items) !== JSON.stringify(initialItems)
    );
  };

  const handleAddItem = () => {
    itemForm.validateFields().then((values) => {
      const medicine = medicines.find(m => m.id === values.medicine_id);
      const selectedLot = selectedStockLots.find(lot => lot.lot_id === values.lot_id);
      
      if (!medicine || !selectedLot) return;

      const newItem: StockOutItem = {
        medicine_id: values.medicine_id,
        medicine_name: medicine.name,
        medicine_code: medicine.code,
        quantity: values.quantity,
        unit: medicine.unit,
        unit_price: isPromotion ? 0 : values.unit_price,
        lot_id: values.lot_id,
        lot_number: selectedLot.lot_number,
        expiry_date: selectedLot.expiry_date,
        total_amount: values.quantity * (isPromotion ? 0 : values.unit_price),
        notes: values.notes || '',
        available_stock: selectedLot.available_quantity
      };

      setItems([...items, newItem]);
      itemForm.resetFields();
      setSelectedMedicine(null);
      setSelectedStockLots([]);
      setIsPromotion(false);
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleMedicineChange = async (medicineId: number) => {
    const medicine = medicines.find(m => m.id === medicineId);
    setSelectedMedicine(medicine || null);
    setSelectedStockLots([]);
    
    if (medicine) {
      try {
        // Lấy thông tin tồn kho theo lô
        const stockLots = await stockService.getMedicineStock(medicineId);
        setSelectedStockLots(stockLots);
        
        itemForm.setFieldsValue({
          unit: medicine.unit,
          available_stock: medicine.current_stock
        });
      } catch (error) {
        console.error('Error fetching medicine stock:', error);
        message.error('Không thể lấy thông tin tồn kho');
      }
    }
  };

  const handleQuantityChange = (quantity: number | null) => {
    if (quantity !== null) {
      const unitPrice = itemForm.getFieldValue('unit_price') || 0;
      itemForm.setFieldsValue({
        total_amount: quantity * unitPrice
      });
    }
  };

  const handleUnitPriceChange = (unitPrice: string | number | null) => {
    if (unitPrice !== null && typeof unitPrice === 'number') {
      const quantity = itemForm.getFieldValue('quantity') || 0;
      itemForm.setFieldsValue({
        total_amount: quantity * unitPrice
      });
    }
  };

  // Update customers list when CustomerSelect fetches data
  const handleCustomersLoaded = (customersList: any[]) => {
    console.log('Customers loaded in StockOutModal:', customersList);
    setCustomers(customersList);
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Tên thuốc',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (text: string, record: StockOutItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Mã: {record.medicine_code || 'N/A'}</div>
        </div>
      ),
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 100,
      render: (_: any, record: StockOutItem) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 500 }}>{record.quantity || 0}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.unit || 'N/A'}</div>
        </div>
      ),
    },
    {
      title: 'Đơn giá',
      key: 'unit_price',
      width: 120,
      render: (_: any, record: StockOutItem) => (
        <div style={{ textAlign: 'right' }}>
          {(record.unit_price || 0).toLocaleString('vi-VN')} VNĐ
        </div>
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total_amount',
      width: 130,
      render: (_: any, record: StockOutItem) => (
        <div style={{ textAlign: 'right', fontWeight: 500 }}>
          {(record.total_amount || 0).toLocaleString('vi-VN')} VNĐ
        </div>
      ),
    },
    {
      title: 'Số lô',
      key: 'lot_number',
      width: 120,
      render: (_: any, record: StockOutItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.lot_number || 'N/A'}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            HSD: {record.expiry_date ? dayjs(record.expiry_date).format('DD/MM/YYYY') : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Ghi chú',
      key: 'notes',
      width: 120,
      render: (_: any, record: StockOutItem) => (
        <div style={{ textAlign: 'center' }}>
          {record.notes || ''}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      render: (_: any, __: any, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
          size="small"
        />
      ),
    },
  ];

  const totalAmount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);

  // Đổi tên onCancel gốc thành handleCancelInternal
  const handleCancelInternal = () => {
    form.resetFields();
    setItems([]);
    onCancel();
  };

  // Thay thế onCancel để xác nhận nếu có dữ liệu chưa lưu
  const handleCancelWithConfirm = () => {
    if (isDirty()) {
      Modal.confirm({
        title: 'Bạn có chắc muốn thoát?',
        content: 'Dữ liệu chưa lưu sẽ bị mất. Bạn có muốn tiếp tục?',
        okText: 'Thoát',
        cancelText: 'Ở lại',
        onOk: () => {
          handleCancelInternal();
        },
      });
    } else {
      handleCancelInternal();
    }
  };

  return (
    <Drawer
      title={
        editingRecord 
          ? `Sửa phiếu xuất kho ${editingRecord.voucher?.voucher_number || editingRecord.reference_number || ''}`
          : 'Tạo phiếu xuất kho'
      }
      open={visible}
      onClose={handleCancelWithConfirm}
      width="87%"
      size="large"
      placement="right"
      style={{ 
        zIndex: 1000
      }}
      bodyStyle={{ 
        padding: '24px',
        height: 'calc(100vh - 110px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh'
      }}
      destroyOnClose
      maskStyle={{ zIndex: 999 }}
      loading={isLoadingDetail}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onFinish={(values) => {
          if (items.length === 0) {
            message.error('Vui lòng thêm ít nhất một thuốc');
            return;
          }
          
          // Validate items have required fields
          const invalidItems = items.filter(item => 
            !item.medicine_id ||
            !item.quantity ||
            item.unit_price === null || item.unit_price === undefined ||
            !item.lot_id
          );
          
          if (invalidItems.length > 0) {
            message.error('Một số thuốc thiếu thông tin bắt buộc');
            return;
          }
          
          // Check if we're editing and have existing items
          if (editingRecord) {
            const existingItems = items.filter(item => item.id);
            console.log('All items:', items);
            console.log('Items with ID:', existingItems);
            console.log('Items without ID:', items.filter(item => !item.id));
            
            // Allow update even if no items have ID - API will handle creating new items
            if (items.length === 0) {
              message.error('Vui lòng thêm ít nhất một thuốc');
              return;
            }
          }
          
          console.log('Form values before submit:', values);
          console.log('Customer ID from form:', values.customer_id);
          console.log('Customer ID type:', typeof values.customer_id);
          
          const submitData = {
            customer_id: values.customer_id || null,
            customer_name: values.customer_name,
            customer_phone: values.customer_phone,
            customer_address: values.customer_address,
            notes: values.notes || '',
            items: items.map(item => {
              const itemData: any = {
                medicine_id: item.medicine_id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                lot_id: item.lot_id,
                lot_number: item.lot_number || 'N/A',
                notes: item.notes || ''
              };
              
              // Only include ID if it exists (for existing items)
              if (item.id) {
                itemData.id = item.id;
              }
              
              // Ensure lot_id is a number or null
              if (itemData.lot_id === undefined || itemData.lot_id === '') {
                itemData.lot_id = null;
              }
              
              return itemData;
            })
          };

          console.log('Submitting data:', submitData);
          console.log('Submit data customer_id:', submitData.customer_id);
          console.log('Items breakdown:', items.map(item => ({
            id: item.id,
            medicine_name: item.medicine_name,
            quantity: item.quantity,
            hasId: !!item.id
          })));
          console.log('Submit data items:', submitData.items);
          onSubmit(submitData);
        }}
      >
        {/* Two Column Layout */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          gap: '24px',
          minHeight: 0,
          overflow: 'hidden'
        }}>
          {/* Left Column - Medicine Details */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <Title level={4} style={{ marginBottom: '16px' }}>
              Chi tiết thuốc xuất kho
              {isLoadingDetail && (
                <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px' }}>
                  (Đang tải dữ liệu...)
                </Text>
              )}
            </Title>
            
            {/* Add Item Form */}
            <div style={{ 
              flexShrink: 0,
              padding: '16px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <Title level={5}>Thêm thuốc</Title>
              <Form form={itemForm} layout="inline">
                <Form.Item
                  name="medicine_id"
                  rules={[{ required: true, message: 'Chọn thuốc' }]}
                >
                  <Select
                    placeholder="Chọn thuốc"
                    style={{ width: 200 }}
                    onChange={handleMedicineChange}
                    showSearch
                    filterOption={(input, option: any) => {
                      const children = option?.children;
                      if (typeof children === 'string') {
                        return children.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                  >
                    {medicines.map(medicine => (
                      <Option key={medicine.id} value={medicine.id}>
                        {medicine.name} ({medicine.code})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="lot_id"
                  rules={[{ required: true, message: 'Chọn lô thuốc' }]}
                >
                  <Select
                    placeholder="Chọn lô thuốc"
                    style={{ width: 200 }}
                    showSearch
                    filterOption={(input, option: any) => {
                      const children = option?.children;
                      if (typeof children === 'string') {
                        return children.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                  >
                    {selectedStockLots.map(lot => (
                      <Option key={lot.lot_id} value={lot.lot_id}>
                        {lot.lot_number} - HSD: {dayjs(lot.expiry_date).format('DD/MM/YYYY')} (Còn: {lot.available_quantity})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item 
                  name="quantity" 
                  rules={[{ required: true, message: 'Nhập số lượng' }]}
                >
                  <InputNumber
                    placeholder="Số lượng"
                    min={1}
                    onChange={handleQuantityChange}
                    style={{ width: 120 }}
                  />
                </Form.Item>

                <Form.Item>
                  <Checkbox checked={isPromotion} onChange={e => setIsPromotion(e.target.checked)}>
                    Hàng khuyến mãi (giá = 0)
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="unit_price"
                  rules={[
                    {
                      required: !isPromotion,
                      message: 'Nhập đơn giá'
                    }
                  ]}
                >
                  <InputNumber
                    placeholder="Đơn giá"
                    min={0}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|,*/g, '')) : 0 as any}
                    onChange={handleUnitPriceChange}
                    style={{ width: 150 }}
                    disabled={isPromotion}
                    value={isPromotion ? 0 : undefined}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddItem}
                  >
                    Thêm
                  </Button>
                </Form.Item>
              </Form>

              {selectedStockLots.length > 0 && (
                <Alert
                  message={`Tồn kho theo lô: ${selectedStockLots.length} lô có sẵn`}
                  description={
                    <div>
                      {selectedStockLots.map(lot => (
                        <div key={lot.lot_id} style={{ marginTop: '4px' }}>
                          • Lô {lot.lot_number || 'N/A'}: {lot.available_quantity || 0} {selectedMedicine?.unit || 'N/A'} 
                          (HSD: {lot.expiry_date ? dayjs(lot.expiry_date).format('DD/MM/YYYY') : 'N/A'})
                        </div>
                      ))}
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              )}
              {selectedMedicine && selectedStockLots.length === 0 && (
                <Alert
                  message={`Không có tồn kho cho thuốc này`}
                  type="warning"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              )}
            </div>

            {/* Items Table */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              minHeight: 0,
              border: '1px solid #d9d9d9',
              borderRadius: '6px'
            }}>
              {editingRecord && items.length > 0 && !isLoadingDetail && (
                <Alert
                  message={`Phiếu hiện tại có ${items.length} thuốc`}
                  description="Bạn có thể thêm thuốc mới hoặc xóa thuốc hiện có"
                  type="info"
                  showIcon
                  style={{ margin: '8px' }}
                />
              )}
              {isLoadingDetail ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#999',
                  border: '1px dashed #d9d9d9',
                  margin: '8px',
                  borderRadius: '6px'
                }}>
                  <div>Đang tải dữ liệu phiếu...</div>
                </div>
              ) : items.length === 0 ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#999',
                  border: '1px dashed #d9d9d9',
                  margin: '8px',
                  borderRadius: '6px'
                }}>
                  <InboxOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>Chưa có thuốc nào được thêm</div>
                  <div style={{ fontSize: '12px' }}>Vui lòng thêm thuốc từ form bên trên</div>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={items}
                  rowKey={(_record, index) => index?.toString() || '0'}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 800, y: 'calc(100vh - 500px)' }}
                />
              )}
            </div>
          </div>

          {/* Right Column - Other Information */}
          <div style={{ 
            width: '400px', 
            display: 'flex', 
            flexDirection: 'column',
            flexShrink: 0,
            overflow: 'hidden'
          }}>
            <Title level={4} style={{ marginBottom: '16px' }}>Thông tin khách hàng</Title>
            
            {/* Customer Information */}
            <div style={{ 
              padding: '16px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#fafafa',
              minHeight: '200px'
            }}>
              
              <Form.Item
                name="customer_id"
                label="Chọn khách hàng"
                rules={[{ required: true, message: 'Vui lòng chọn khách hàng' }]}
                style={{ marginBottom: '16px' }}
              >
                <CustomerSelect
                  placeholder="Chọn khách hàng từ danh sách"
                  onChange={(value, customer) => {
                    console.log('CustomerSelect onChange - value:', value, 'customer:', customer);
                    if (customer) {
                      const formValues = {
                        customer_id: value,
                        customer_name: customer.name || '',
                        customer_phone: customer.phone || '',
                        customer_address: customer.address || ''
                      };
                      console.log('Setting form values:', formValues);
                      form.setFieldsValue(formValues);
                    }
                  }}
                  onCustomersLoaded={handleCustomersLoaded}
                />
              </Form.Item>

              {/* Hidden fields to ensure customer info is submitted */}
              <Form.Item name="customer_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              <Form.Item name="customer_phone" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              <Form.Item name="customer_address" style={{ display: 'none' }}>
                <Input />
              </Form.Item>

              <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                Không tìm thấy khách hàng? 
                <Button 
                  type="link" 
                  size="small" 
                  style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                  onClick={() => {
                    message.info('Vui lòng tạo khách hàng mới trong trang Quản lý khách hàng');
                  }}
                >
                  Tạo khách hàng mới
                </Button>
              </div>

              <Form.Item
                name="customer_phone"
                label="Số điện thoại"
                rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}
                style={{ marginBottom: '16px' }}
              >
                <Input 
                  placeholder="Số điện thoại sẽ tự động điền khi chọn khách hàng"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="customer_address"
                label="Địa chỉ"
                rules={[{ required: true, message: 'Vui lòng nhập địa chỉ' }]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Địa chỉ sẽ tự động điền khi chọn khách hàng"
                  style={{ width: '100%', resize: 'none' }}
                />
              </Form.Item>
            </div>

            {/* Summary */}
            <div style={{ 
              flexShrink: 0,
              padding: '16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: '#f0f8ff',
              minHeight: '120px'
            }}>
              <Title level={5} style={{ marginBottom: '12px', fontSize: '16px' }}>Tổng kết</Title>
              <div style={{ marginBottom: '8px' }}>
                <Text>Số lượng thuốc: </Text>
                <Text strong>{items.length}</Text>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <Text>Tổng số lượng: </Text>
                <Text strong>{items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</Text>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ textAlign: 'right' }}>
                <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                  Tổng tiền: {(totalAmount || 0).toLocaleString('vi-VN')} VNĐ
                </Text>
              </div>
            </div>

            {/* Notes */}
            <div style={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden'
            }}>
              <Form.Item 
                name="notes" 
                label="Ghi chú"
                style={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 0
                }}
              >
                <TextArea 
                  rows={6} 
                  placeholder="Nhập ghi chú (nếu có)"
                  style={{ 
                    resize: 'none',
                    flex: 1,
                    minHeight: '120px'
                  }}
                />
              </Form.Item>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              flexShrink: 0,
              textAlign: 'right', 
              marginTop: '16px',
              padding: '16px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              borderRadius: '0 0 6px 6px'
            }}>
              <Space>
                <Button onClick={handleCancelWithConfirm}>
                  Hủy
                </Button>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                  {editingRecord ? 'Cập nhật' : 'Lưu phiếu'}
                </Button>
              </Space>
            </div>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default StockOutModal; 